import { useMutation } from "@apollo/client";
import _ from "lodash";
import { useContext, useEffect, useState } from "react";
import { GlobalConfigContext } from "../../../../context/GlobalConfigContext";
import updateProgramConfiguration from "../../../../graphql/mutations/updateProgramConfiguration";

/**
 * Hook for managing configuration forms
 * @param {string} configKey - The configuration key (e.g., 'partner_configuration')
 * @param {Object} validators - Field validators object with function for each field
 */
export default function useConfigurationForm(configKey, validators = {}) {
  const {
    programConfigurations,
    updateProgramConfigurations,
    globalState: { programConfig },
  } = useContext(GlobalConfigContext);

  const [updateProgramConfigurationMutation] = useMutation(
    updateProgramConfiguration
  );

  const [isUpdated, setIsUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFieldErrors, setHasFieldErrors] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [formData, setFormData] = useState({});

  // Load configuration data
  useEffect(() => {
    const configData = _.find(programConfigurations, { key: configKey });

    if (configData?.value?.data) {
      setInitialFormData((prev) => ({
        ...prev,
        ...configData.value.data,
      }));
      setFormData((prev) => ({
        ...prev,
        ...configData.value.data,
      }));
    }
    setIsLoading(false);
  }, [programConfigurations, configKey]);

  // Check fields validation status to enable/disable save button
  useEffect(() => {
    const hasAnyNestedError = (obj) => {
      if (typeof obj !== "object" || obj === null) {
        return obj !== null && obj !== false;
      }

      return Object.values(obj).some((value) => hasAnyNestedError(value));
    };

    const hasAnyError = hasAnyNestedError(fieldErrors);

    const hasEmptyRequiredField = Object.entries(validators).some(
      ([path, validator]) => {
        if (!validator?.required) return false;
        const value = getDeepValue(formData, path);
        return !value?.toString().trim?.(); // Handles strings/numbers
      }
    );
    setHasFieldErrors(hasAnyError || hasEmptyRequiredField);
  }, [formData, fieldErrors, validators]);

  // Compare initialFormData and formData to detect changes
  useEffect(() => {
    const isEqual = JSON.stringify(initialFormData) === JSON.stringify(formData);
    setIsUpdated(!isEqual);
  }, [initialFormData, formData]);

  const validateField = (name, value) => {
    const validator = getDeepValue(validators, name);

    if (validator?.validate) {
      return validator.validate(value);
    }

    if (validator?.required && value.trim?.() === "") {
      return "This field is required";
    }

    return false;
  };

  useEffect(() => {}, [formData, fieldErrors, hasFieldErrors]);

  const setDeepValue = (obj, path, value) => {
    const keys = path
      .replace(/\[(\w+)\]/g, ".$1")
      .replace(/^\./, "")
      .split(".");

    const newObj = { ...obj };

    let current = newObj;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];

      if (!(key in current) || typeof current[key] !== "object") {
        // Create object or array based on next key
        const nextKey = keys[i + 1];
        current[key] = /^\d+$/.test(nextKey) ? [] : {};
      }

      current[key] = Array.isArray(current[key])
        ? [...current[key]]
        : { ...current[key] };

      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
    return newObj;
  };

  const getDeepValue = (obj, path) => {
    const keys = path
      .replace(/\[(\w+)\]/g, ".$1")
      .replace(/^\./, "")
      .split(".");

    return keys.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Ensure value is always a string for consistent validation
    const stringValue = typeof value === "number" ? value.toString() : value;

    setFormData((prev) => setDeepValue(prev, name, stringValue));

    const error = validateField(name, stringValue);

    setFieldErrors((prev) => setDeepValue(prev, name, error));
  };

  const openSuccessToast = () => {
    setToast({
      open: true,
      type: "success",
      title: "Settings updated successfully!",
      description: `You have just saved the ${configKey.replace("_", " ")}`,
      navigateToSettings: true,
    });
  };

  const openErrorToast = () => {
    setToast({
      open: true,
      type: "error",
      title: "Something went wrong!",
      description: "Please try again",
    });
  };

  const handleSaveConfirmation = async () => {
    const params = {
      variables: {
        input: {
          slug: configKey,
          data: JSON.stringify(formData),
        },
        id: programConfig?.id,
      },
    };

    try {
      setIsLoading(true);
      const res = await updateProgramConfigurationMutation(params);
      if (res?.data?.updateProgramConfiguration) {
        updateProgramConfigurations(
          res?.data?.updateProgramConfiguration?.configurations
        );
        openSuccessToast();
        setIsLoading(false);
      }
    } catch {
      openErrorToast();
      setIsLoading(false);
    }

    setConfirmationModalOpen(false);
  };

  return {
    formData,
    initialFormData,
    fieldErrors,
    isUpdated,
    isLoading,
    hasFieldErrors,
    confirmationModalOpen,
    discardModalOpen,
    toast,
    handleInputChange,
    handleSaveConfirmation,
    setConfirmationModalOpen,
    setDiscardModalOpen,
    setToast,
    validateField,
  };
}
